import { useCustomSnackbar } from '@amagimedia/amagi-design-system';
import { Typography } from '@amagimedia/amagi-design-system/src/Typography/Typography';
import {
  deleteCookie,
  handleCookieSplit,
  setCookie,
} from '@amagimedia/api-client';
import {
  ACCESS_TOKEN_PART_1_KEY,
  ACCESS_TOKEN_PART_2_KEY,
  ACCESS_TOKEN_PART_3_KEY,
  REFRESH_TOKEN_KEY,
} from '@amagimedia/api-client/src/constants';
import {
  AccessTokenPart1Cookie,
  AccessTokenPart2Cookie,
  AccessTokenPart3Cookie,
  RefreshTokenKeyCookie,
} from '@amagimedia/api-client/src/types';
import { APP_BASE_DOMAIN } from '@amagimedia/commons';
import { amagiAPIClientStvV2 } from '@amagimedia/commons/src/ApiClientStvV2';
import { getAxiosErrorMessageAsString } from '@amagimedia/commons/src/utils';
import { useEffect, type FC } from 'react';
import { useHistory } from 'react-router-dom';
import { HomeScreenContainer } from './HomeScreenContainer';

interface ISigningInProps {}

export function useAuthCode() {
  const {
    location: { search },
  } = useHistory();
  const searchParams = new URLSearchParams(search);
  return searchParams.get('code') || '';
}

export function deleteMultipleCookies(cookieKeys: string[], domain: string) {
  cookieKeys.forEach((key) => {
    deleteCookie(key, `.${domain}`);
  });
}

function setMultipleCookies(data: ISetMultipleCookieData[], domain: string) {
  data.forEach((d) => {
    setCookie({
      cookieKey: d.key,
      cookieData: JSON.stringify(d.value),
      expires: new Date('Fri, 31 Dec 9999 21:10:10 GMT'),
      domain: `.${domain}`,
    });
  });
}

interface ISetMultipleCookieData {
  key: string;
  value:
    | AccessTokenPart1Cookie
    | AccessTokenPart2Cookie
    | AccessTokenPart3Cookie
    | RefreshTokenKeyCookie;
}

export const SigningIn: FC<ISigningInProps> = () => {
  const { push } = useHistory();
  const showSnackbar = useCustomSnackbar();
  const hostname = window.location.hostname;
  const splitHostName = hostname.split('.');
  const amg_id = splitHostName[0];
  const code = useAuthCode();

  useEffect(() => {
    amagiAPIClientStvV2
      .generateTokenApiStvV2(amg_id, code, 'authorization_code')
      .then((res) => {
        let domain = '';
        if (APP_BASE_DOMAIN) {
          domain = APP_BASE_DOMAIN;
        }
        deleteMultipleCookies(
          [
            ACCESS_TOKEN_PART_1_KEY,
            ACCESS_TOKEN_PART_2_KEY,
            ACCESS_TOKEN_PART_3_KEY,
            REFRESH_TOKEN_KEY,
          ],
          `.${domain}`,
        );

        const [access_token_1, access_token_2, access_token_3] =
          handleCookieSplit(res.data.access_token);
        const accessToken1Data: AccessTokenPart1Cookie = {
          [ACCESS_TOKEN_PART_1_KEY]: access_token_1,
        };
        const accessToken2Data: AccessTokenPart2Cookie = {
          [ACCESS_TOKEN_PART_2_KEY]: access_token_2,
        };
        const accessToken3Data: AccessTokenPart3Cookie = {
          [ACCESS_TOKEN_PART_3_KEY]: access_token_3,
        };
        const refreshTokenData: RefreshTokenKeyCookie = {
          [REFRESH_TOKEN_KEY]: res.data.refresh_token,
        };

        setMultipleCookies(
          [
            { key: ACCESS_TOKEN_PART_1_KEY, value: accessToken1Data },
            { key: ACCESS_TOKEN_PART_2_KEY, value: accessToken2Data },
            { key: ACCESS_TOKEN_PART_3_KEY, value: accessToken3Data },
            { key: REFRESH_TOKEN_KEY, value: refreshTokenData },
          ],
          `.${domain}`,
        );
        window.location.assign(`https://${res.data.domain}/partner`);
        return;
      })
      .catch((e) => {
        showSnackbar(getAxiosErrorMessageAsString(e), 'error');
      });
  }, [amg_id, code, showSnackbar, push]);

  return (
    <HomeScreenContainer>
      <Typography type="headingH1">Signing in...</Typography>
    </HomeScreenContainer>
  );
};
